import React, { useState, useEffect, useCallback, useRef, Fragment, useMemo } from 'react';

import {
  Button,
  FormControl,
  ButtonToolbar,
  InputGroup,
  Row,
  Col,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { Lens, IcoPlus, TooltipInfoIcon, DownloadFile } from '@icons';
import {
  getTranslations,
  getStats,
  getProjects,
  getInterventions,
  getFinancings,
  getStructureClusters,
  exportExcel,
  getEsgTimeSeries,
  getSroiTimeSeries,
  getChartStats,
} from '@lib/api';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css';
import AG_GRID_LOCALE_IT from '@components/projects/ag-grid-translate/locale-it';
import ActionBtnRenderer from '@components/projects/actionBtnRenderer/ActionBtnRenderer';
import RadarChartGradient from '@components/projects/radarChart/RadarChart';
import EsgTimingBarChart from 'components/charts/esgTimingBarChart';
import SroiTimingBarChart from 'components/charts/sroiTimingBarChart';
import {
  calculateAverageScore,
  filterAndSortByValue,
  formatValue,
  mapProponentData,
  mapScoreToRating,
  mergeAvgAndStdDev,
} from '@lib/helpers/homepageHelpers';
import { proponentsOptionsLabel } from '@lib/const/selectOptionsConstants';
import HomeDonutChart from 'components/charts/homeDonutChart';
import { MetricsTable } from 'components/metricsTable/metricsTable.js';
import { DangerToast } from 'components/GlobalToastList';
import RegionalHeatmapChart from 'components/charts/regionalHeatmapChart';
import DistributionAverageVerticalHistogram from 'components/charts/distributionAverageVerticalHistogram';
import SingleBarVerticalHistogram from 'components/charts/singleBarVerticalHistogram';

const esgLabel = ['E', 'S', 'G', 'ESG'];

const ProjectsTab = () => {
  const [translations, setTranslations] = useState([]);
  const [projects, setProjects] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [financing, setFinancing] = useState([]);
  const [cluster, setCluster] = useState([]);
  const [optionsPagination, setOptionsPagination] = useState([]);
  const [stats, setStats] = useState({});
  const [dataRating, setDataRating] = useState(0);
  const [avgRating, setAvgRating] = useState('');
  const [scoreEsg, setScoreEsg] = useState('');
  const [dataBenefits, setDataBenefits] = useState([]);
  const [dataSroi, setDataSroi] = useState([]);
  const [avgBenefits, setAvgBenefits] = useState([]);
  const [avgData, setAvgData] = useState([]);
  const [noOutData, setNoOutData] = useState([]);
  const [esgTimeSeriesRating, setEsgTimeSeriesRating] = useState([]);
  const [sroiTimeSeriesRating, setSroiTimeSeriesRating] = useState([]);
  const gridRef = useRef();
  const [refresh, setRefresh] = useState(false);
  const localeText = AG_GRID_LOCALE_IT;
  const [isLoading, setIsLoading] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [chartsData, setChartsData] = useState({});

  useEffect(() => {
    let isCancelled = false;

    const loadDashboardData = async () => {
      setIsDashboardLoading(true);

      try {
        const [
          retrievedTranslation,
          retrievedStats,
          retrievedInterventions,
          retrievedFinancings,
          retrievedCluster,
          retrievedChartsStats,
        ] = await Promise.all([
          getTranslations(),
          getStats(),
          getInterventions(),
          getFinancings(),
          getStructureClusters(),
          getChartStats(),
        ]);

        if (isCancelled) return;

        setTranslations(retrievedTranslation);

        // 👇 salva charts data
        setChartsData(retrievedChartsStats);

        if (retrievedStats && Object.keys(retrievedStats).length > 0) {
          const {
            avgRating,
            avgScoreNoOut,
            stdDevNoOut,
            stdDevSroiProponentNoOut,
            avgProponentSroiNoOut,
            totalProponentBenefits,
            stdDevSroiProponent,
            avgProponentSroi,
            avgPortfolioValue,
            esgOutlook,
          } = retrievedStats;

          setStats(retrievedStats);

          setAvgData([
            { name: 'Rating Medio', ...avgRating },
            { name: 'Score Medio', ...avgScoreNoOut },
            { name: 'Deviazione Standard', ...stdDevNoOut },
          ]);

          setNoOutData(mergeAvgAndStdDev(avgProponentSroiNoOut, stdDevSroiProponentNoOut));

          setDataBenefits(
            mapProponentData(filterAndSortByValue(totalProponentBenefits), proponentsOptionsLabel),
          );

          setAvgBenefits(
            mapProponentData(filterAndSortByValue(stdDevSroiProponent), proponentsOptionsLabel).map(
              (item) => ({ ...item, type: 'avg' }),
            ),
          );

          setDataSroi(
            mapProponentData(filterAndSortByValue(avgProponentSroi), proponentsOptionsLabel),
          );

          setScoreEsg(formatValue(avgPortfolioValue));
          setDataRating(esgOutlook?.percentage || 0);
        }

        setInterventions(retrievedInterventions);
        setFinancing(retrievedFinancings);
        setCluster(retrievedCluster);
      } catch (error) {
        if (!isCancelled) {
          console.error('Errore durante il caricamento delle statistiche/dashboard:', error);
          DangerToast(
            'Attenzione',
            'Si è verificato un errore durante il caricamento dei dati della dashboard',
          );
        }
      } finally {
        if (!isCancelled) {
          setIsDashboardLoading(false);
        }
      }
    };

    loadDashboardData();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    getEsgTimeSeries().then((retrievedEsgTimeSeries) => {
      setEsgTimeSeriesRating(retrievedEsgTimeSeries);
    });

    getSroiTimeSeries().then((retrievedSroiTimeSeries) => {
      setSroiTimeSeriesRating(retrievedSroiTimeSeries);
    });
  }, []);

  useEffect(() => {
    setOptionsPagination([
      <Fragment key='options'>
        <option defaultValue='20' key='20'>
          20
        </option>
        <option value='50' key='50'>
          50
        </option>
        <option value='100' key='100'>
          100
        </option>
      </Fragment>,
    ]);
  }, []);

  useEffect(() => {
    if (stats?.avgPortfolio) {
      setAvgRating(stats.avgPortfolio);
      return;
    }

    if (projects?.length > 0) {
      const avgScore = calculateAverageScore(projects);
      setAvgRating(mapScoreToRating(avgScore));
    }
  }, [projects, stats]);

  useEffect(() => {
    if (refresh) {
      getProjects().then((retrievedProjects) => {
        setProjects(retrievedProjects);
      });
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    functionDrawRadarGradient();
  }, [dataBenefits]);

  useEffect(() => {
    if (isLoading) {
      exportExcel().then(() => {});
      setIsLoading(false);
    }
  }, [isLoading]);

  const functionDrawRadarGradient = (data, secondData, color, secondColor, divId, maxValue) => {
    const axisIndexMap = {};
    data?.forEach((item, index) => {
      axisIndexMap[item.axis] = index;
    });

    secondData?.sort((a, b) => {
      const indexA = axisIndexMap[a.axis];
      const indexB = axisIndexMap[b.axis];
      return indexA - indexB;
    });

    const mergedData = data && secondData && [...data, ...secondData];
    const highestValue = mergedData && Math.max(...mergedData?.map((el) => el.value));

    return (
      <RadarChartGradient
        data={[data, secondData]}
        radarColor={color}
        secondRadarColor={secondColor}
        divId={divId}
        maxValue={highestValue || maxValue}
      />
    );
  };

  const onFilterTextBoxChanged = useCallback((e) => {
    gridRef.current.api.setQuickFilter(e.target.value);
  }, []);

  const onGridReady = useCallback(() => {
    getProjects().then((retrievedProjects) => {
      console.log('CLUSTER', retrievedProjects);
      setProjects(retrievedProjects);
    });
  }, []);

  const autoSizeAll = useCallback((skipHeader) => {
    const allColumnIds = [];
    gridRef.current.columnApi.getAllColumns().forEach((column) => {
      allColumnIds.push(column.getId());
    });
    gridRef.current.columnApi.autoSizeColumns(allColumnIds, skipHeader);
  }, []);

  const onFirstDataRendered = useCallback(() => {
    if (window.innerWidth >= 2540) {
      gridRef.current.api.sizeColumnsToFit();
    } else {
      autoSizeAll(false);
    }
  }, [autoSizeAll]);

  const onModelUpdated = useCallback(() => {
    if (window.innerWidth >= 2540) {
      gridRef.current.api.sizeColumnsToFit();
    } else {
      autoSizeAll(false);
    }
  }, [autoSizeAll]);

  const onPageSizeChanged = useCallback(() => {
    let value = document.getElementById('page-size').value;
    gridRef.current.api.paginationSetPageSize(Number(value));
  }, []);

  const jsxInCell = (params) => {
    return (
      <div className='d-flex justify-content-between align-items-center'>
        {params.data.requestId} <TooltipInfoIcon style={{ width: '1rem', minWidth: '1rem' }} />
      </div>
    );
  };

  const defaultColDef = {
    resizable: true,
    wrapText: true,
    autoHeight: true,
    flex: 1,
    suppressMovable: true,
    cellStyle: { padding: '0 0.7rem' },
  };

  const columnDefs = [
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS002')
        .map((trans) => trans.text),
      field: 'clientId',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS003')
        .map((trans) => trans.text),
      field: 'requestId',
      filter: 'agTextColumnFilter',
      tooltipField: 'description',
      cellRendererSelector: () => ({
        component: jsxInCell,
      }),
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS005')
        .map((trans) => trans.text),
      field: 'proponents',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data.proponents.name,
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS033')
        .map((trans) => trans.text),
      field: 'interventionClassCode',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const interventionName = params.data.interventionClassCode
          ? interventions
              .filter((intervention) => intervention.code === params.data.interventionClassCode)
              .map((filteredIntervention) => filteredIntervention.text)
          : financing
              .filter((financingItem) => financingItem.code === params.data.financingTypeCode)
              .map((filteredFinancing) => filteredFinancing.text);

        return interventionName;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS008')
        .map((trans) => trans.text),
      field: 'sector',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const sectorName = params.data.structureClusterCode
          ? cluster
              .filter((clusterItem) => clusterItem.code === params.data.structureClusterCode)
              .map((filteredCluster) => `${params.data.sectors.text} - ${filteredCluster.cluster}`)
          : params.data.sectors.text;
        return sectorName;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS009')
        .map((trans) => trans.text),
      field: 'regions',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data.locations.nomeRegione,
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS095')
        .map((trans) => trans.text),
      field: 'sroiratings',
      cellStyle: { textAlign: 'right' },
      valueGetter: (params) => {
        if (params.data.interventionClassCode !== 'NOINTPURCH') {
          const totalCapex = params.data.sroiratings
            .filter((esgrating) => esgrating.code === 'TOTALCAPEX')
            .map((filteredRating) =>
              filteredRating.value.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            );
          return totalCapex.length > 0 ? `${totalCapex} €` : '';
        } else {
          return params.data?.totalInvestment
            ? `${params.data.totalInvestment.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} €`
            : '';
        }
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS053')
        .map((trans) => trans.text),
      field: 'esgratings',
      cellStyle: { textAlign: 'center' },
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals'],
        defaultOption: 'equals',
      },
      valueGetter: (params) => {
        const rating = params.data.esgratings
          .filter((esgrating) => esgrating.type === 'total')
          .map((filteredRating) => filteredRating.rating);

        return rating;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS096')
        .map((trans) => trans.text),
      field: 'sroiratings',
      filter: 'agTextColumnFilter',
      cellStyle: { textAlign: 'right' },
      valueGetter: (params) => {
        if (params.data.interventionClassCode !== 'NOINTPURCH') {
          return params.data.sroiratings
            .filter((sroi) => sroi.code === 'SROI')
            ?.map((filteredValue) =>
              filteredValue.value.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            );
        } else {
          return 'N.A.';
        }
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS052')
        .map((trans) => trans.text),
      field: 'status',
      cellStyle: (params) =>
        params.data.status !== 'DRAFT'
          ? { color: '#08a2aa', fontWeight: 'bold', padding: '0 0.7rem' }
          : { color: 'black', padding: '0 0.7rem' },
      pinned: 'right',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const status = params.data.status;
        return status === 'DRAFT' ? 'Bozza' : 'Inviato';
      },
    },
    {
      headerName: '',
      pinned: 'right',
      minWidth: 170,
      maxWidth: 200,
      cellStyle: { textAlign: 'center' },
      cellRenderer: ActionBtnRenderer,
      cellRendererParams: {
        setRefresh: setRefresh,
      },
    },
  ];

  const handleDownload = () => {
    setIsLoading(true);
  };

  const sroiDetailColumns = [{ key: 'value', label: '' }];

  const sroiDetailRows = [
    {
      id: 'avgVanBenefits',
      labelNode: (
        <>
          {translations
            .filter((trans) => trans.code === 'AVGVANBENEFITS')
            .map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === 'AVGVANBENEFITS')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgvanbenefits-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'VANBENEFITS')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value:
        stats && stats.totalBenefits
          ? `${stats.totalBenefits.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} €`
          : 'N/A',
    },
    {
      id: 'avgVanCosts',
      labelNode: (
        <>
          {translations.filter((trans) => trans.code === 'AVGVANCOSTS').map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === 'AVGVANCOSTS')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgvancosts-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'VANCOSTS')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value:
        stats && stats.totalCosts
          ? `${stats.totalCosts.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} €`
          : 'N/A',
    },
    {
      id: 'avgVane',
      labelNode: (
        <>
          {translations.filter((trans) => trans.code === 'AVGVANE').map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === 'AVGVANE')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgvane-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'VANE')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value:
        stats && stats.totalVANE
          ? `${stats.totalVANE.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} €`
          : 'N/A',
    },
    {
      id: 'avgSroi',
      labelNode: (
        <>
          {translations.filter((trans) => trans.code === 'AVGSROI').map((trans) => trans.text)}

          <OverlayTrigger
            placement='right'
            delay={{ show: 200, hide: 400 }}
            overlay={
              <div
                className='d-flex p-3 f-500'
                style={{
                  backgroundColor: 'rgba(0,0,0,0.9)',
                  color: '#ffffff',
                  fontSize: '.9rem',
                  borderRadius: '5px',
                }}>
                <span className='mr-2'>
                  {translations
                    .filter((trans) => trans.code === 'ICS132')
                    .map((trans) => trans.text)}
                </span>
                <span>
                  ({' '}
                  {translations
                    .filter((trans) => trans.code === 'ICS133')
                    .map((trans) => trans.text)}{' '}
                  )
                </span>
              </div>
            }>
            <TooltipInfoIcon className='tooltip-ico mb-1' />
          </OverlayTrigger>

          {translations
            .filter((trans) => trans.code === 'AVGSROI')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgsroi-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'AVGSROI')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value: (
        <>
          {stats && stats.avgSroi
            ? stats.avgSroi.toLocaleString('it-IT', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            : 'N/A'}
          <span className='ml-2 f-300'>
            {noOutData &&
              noOutData
                .filter((el) => el.proponent === 'portfolio')
                .map(
                  (el) =>
                    `(${el.avgValue.toLocaleString('it-IT', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })})`,
                )}
          </span>
        </>
      ),
    },
    {
      id: 'ics131',
      labelNode: (
        <span>
          {translations.filter((trans) => trans.code === 'ICS131').map((trans) => trans.text)}

          <OverlayTrigger
            placement='right'
            delay={{ show: 200, hide: 400 }}
            overlay={
              <div
                className='d-flex p-3 f-500'
                style={{
                  backgroundColor: 'rgba(0,0,0,0.9)',
                  color: '#ffffff',
                  fontSize: '.9rem',
                  borderRadius: '5px',
                }}>
                <span className='mr-2'>
                  {translations
                    .filter((trans) => trans.code === 'ICS132')
                    .map((trans) => trans.text)}
                </span>
                <span>
                  ({' '}
                  {translations
                    .filter((trans) => trans.code === 'ICS133')
                    .map((trans) => trans.text)}{' '}
                  )
                </span>
              </div>
            }>
            <TooltipInfoIcon className='tooltip-ico mb-1' />
          </OverlayTrigger>
        </span>
      ),
      value: (
        <>
          {stats &&
            stats?.stdDevSroiProponent
              ?.filter((el) => el.proponent === 'portfolio')
              .map((portfolioStat) =>
                portfolioStat.value.toLocaleString('it-IT', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }),
              )}
          <span className='ml-2 f-300'>
            {noOutData &&
              noOutData
                ?.filter((el) => el.proponent === 'portfolio')
                .map(
                  (el) =>
                    `(${el.stdDevValue.toLocaleString('it-IT', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })})`,
                )}
          </span>
        </>
      ),
    },
  ];
  const financingSroiTable = useMemo(() => {
    const rawData = chartsData?.sroiAveragePond;

    if (!rawData || typeof rawData !== 'object' || !Array.isArray(financing)) {
      return [];
    }

    const labelsMap = financing.reduce((acc, item) => {
      if (item?.code && item?.text) {
        acc[item.code.toUpperCase()] = item.text;
      }
      return acc;
    }, {});

    const normalizedData = Object.entries(rawData).reduce((acc, [key, value]) => {
      const upperKey = key.toUpperCase();

      if (labelsMap[upperKey] && acc[upperKey] == null) {
        acc[upperKey] = Number(value);
      }

      return acc;
    }, {});

    return Object.entries(normalizedData)
      .sort((a, b) => labelsMap[a[0]].localeCompare(labelsMap[b[0]], 'it'))
      .map(([code, value]) => ({
        id: code,
        labelNode: <p className='mb-0'>{labelsMap[code]}</p>,
        value: Number.isFinite(value)
          ? value.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })
          : 'N/A',
      }));
  }, [chartsData?.sroiAveragePond, financing]);

  const downloadTooltip = (props) => <Tooltip {...props}>Download Excel</Tooltip>;

  return (
    <>
      <div className='dashboard dashboard-stack'>
        <Row className='report-first gap-3 p-0 d-flex'>
          <Col className='simple-box report-1 mt-0'>
            <Row>
              <h5 className='title-simple-box text-uppercase mt-1 pl-3'>Riepilogo</h5>
            </Row>
            <Row className='mt-3'>
              {Object.keys(stats).length > 0 && !isDashboardLoading ? (
                <>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>numerosità</p>
                    <p className='my-4 font-lg-2'>{stats.projectsSubmitted}</p>
                    <p className='text-uppercase f-500 min-label-width'>progetti</p>
                  </Col>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>euro</p>
                    <p className='my-4 font-lg-2'>
                      {stats.totalInvestments?.toLocaleString('it-IT', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <p className='text-uppercase f-500 min-label-width position-relative'>
                      {translations
                        .filter((trans) => trans.code === 'ICS043')
                        .map((trans) => trans.text)}
                      <OverlayTrigger
                        placement='bottom'
                        delay={{ show: 200, hide: 400 }}
                        overlay={
                          <Tooltip id='button-tooltip'>
                            {translations
                              .filter((trans) => trans.code === 'ICS043')
                              .map((trans) => trans.description)}
                          </Tooltip>
                        }>
                        <TooltipInfoIcon
                          className='tooltip-ico'
                          style={{
                            width: '1rem',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </OverlayTrigger>
                    </p>
                  </Col>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>euro</p>
                    <p className='my-4 font-lg-2'>
                      {stats.avgInvestment?.toLocaleString('it-IT', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <p className='text-uppercase f-500 min-label-width position-relative'>
                      {translations
                        .filter((trans) => trans.code === 'ICS044')
                        .map((trans) => trans.text)}
                      <OverlayTrigger
                        placement='bottom'
                        delay={{ show: 200, hide: 400 }}
                        overlay={
                          <Tooltip id='button-tooltip'>
                            {translations
                              .filter((trans) => trans.code === 'ICS044')
                              .map((trans) => trans.description)}
                          </Tooltip>
                        }>
                        <TooltipInfoIcon
                          className='tooltip-ico'
                          style={{
                            width: '1rem',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </OverlayTrigger>
                    </p>
                  </Col>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>anni</p>
                    <p className='my-4 font-lg-2'>
                      {stats.avgDuration?.toLocaleString('it-IT', { maximumFractionDigits: 1 })}
                    </p>
                    <p className='text-uppercase f-500 min-label-width'>durata media</p>
                  </Col>
                </>
              ) : (
                <div className='d-flex justify-content-center align-items-center w-100'>
                  <Spinner animation='border' role='status' className='my-3'>
                    <span style={{ visibility: 'hidden' }}>Loading...</span>
                  </Spinner>
                </div>
              )}
            </Row>
          </Col>
        </Row>

        <Row className='report-first gap-3 p-0 d-flex'>
          <Col className='simple-box justify-content-center align-items-center text-center'>
            <Row className='ml-0'>
              <h5 className='title-simple-box text-uppercase mt-1 pl-0'>
                {translations.filter((trans) => trans.code === 'ICS126').map((trans) => trans.text)}
              </h5>
            </Row>

            {dataRating && !isDashboardLoading ? (
              <Row className='mt-5 text-center'>
                <Col
                  id='donut-ratingEsg'
                  className='d-flex flex-column justify-content-center align-items-center'>
                  <HomeDonutChart chartId='ratingEsg' data={dataRating} scoreEsg={scoreEsg} />
                  <p className='text-uppercase f-500 min-label-width mt-4 position-relative'>
                    {translations
                      .filter((trans) => trans.code === 'ICS045')
                      .map((trans) => trans.text)}
                    <OverlayTrigger
                      placement='bottom'
                      delay={{ show: 200, hide: 400 }}
                      overlay={
                        <Tooltip id='button-tooltip'>
                          {translations
                            .filter((trans) => trans.code === 'ICS045')
                            .map((trans) => trans.description)}
                        </Tooltip>
                      }>
                      <TooltipInfoIcon
                        className='tooltip-ico'
                        style={{
                          width: '1rem',
                          position: 'absolute',
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      />
                    </OverlayTrigger>
                  </p>
                </Col>

                <Col id='donut-legend' className='align-self-start'>
                  <Row className='flex-column align-content-center'>
                    <Row className='align-items-center'>
                      <span>Rating:</span>
                    </Row>

                    <Row className='min-label-width align-items-center'>
                      <div className='box green'></div>
                      <div className='legend-text'>
                        A+ <span>|</span> AA <span>|</span> AA+ <span>|</span> AAA
                        <br />
                        (score 60-100)
                      </div>
                    </Row>

                    <Row className='min-label-width align-items-center'>
                      <div className='box yellow'></div>
                      <div className='legend-text'>
                        BB <span>|</span> BBB <span>|</span> A
                        <br />
                        (score 30-60)
                      </div>
                    </Row>

                    <Row className='min-label-width align-items-center'>
                      <div className='box red'></div>
                      <div className='legend-text'>
                        C <span>|</span> B
                        <br />
                        (score 0-30)
                      </div>
                    </Row>
                  </Row>
                </Col>

                <Col id='media-portafoglio' className='text-center justify-content-center'>
                  <div style={{ width: 'fit-content', margin: '0 auto' }}>
                    <p className='mb-4 font-lg-2'>{avgRating === '' ? 'N/A' : avgRating}</p>
                    <p className='media-text text-uppercase f-500'>media portafoglio</p>
                  </div>
                </Col>
              </Row>
            ) : (
              <div className='d-flex justify-content-center align-items-center w-100'>
                <Spinner animation='border' role='status' className='my-3'>
                  <span style={{ visibility: 'hidden' }}>Loading...</span>
                </Spinner>
              </div>
            )}
          </Col>
        </Row>

        <div className='d-flex justify-content-between px-0 col-12 flex-row gap-1 flex-wrap dashboard-block'>
          <div className='p-0 d-flex' style={{ minWidth: '863.33px', flex: '1 1 863.33px' }}>
            <Col className='simple-box justify-content-center col-12'>
              <Row className='ml-0'>
                <h5 className='title-simple-box text-uppercase mt-1 pl-0'>
                  {translations
                    .filter((trans) => trans.code === 'ICS127')
                    .map((trans) => trans.text)}
                </h5>
              </Row>
              {Object.keys(stats).length > 0 && dataBenefits.length > 0 && !isDashboardLoading ? (
                <Row
                  className={`sroi-details-padding d-flex ${
                    window.innerWidth <= 832 ? 'flex-column' : ''
                  } align-items-center`}>
                  <Col>
                    <Row id='sroi-details' style={{ minWidth: '400px' }}>
                      <Col className={`${window.innerWidth <= 832 ? 'mb-5' : ''}`}>
                        <MetricsTable
                          data={sroiDetailRows}
                          columns={sroiDetailColumns}
                          rowsPerColumn={5}
                          breakpoint={832}
                          showHeader={false}
                          valueRight={true}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col
                    className='align-items-center text-center align-self-center mt-5'
                    id='radarSroi'>
                    <Row className='align-items-center justify-content-center text-center'>
                      {functionDrawRadarGradient(
                        dataSroi,
                        avgBenefits,
                        '#C4C4C4',
                        '#ffff00',
                        'radarSroi',
                        5,
                      )}
                    </Row>
                  </Col>
                </Row>
              ) : (
                <div className='d-flex justify-content-center align-items-center'>
                  <Spinner animation='border' role='status' className='my-3'>
                    <span style={{ visibility: 'hidden' }}>Loading...</span>
                  </Spinner>
                </div>
              )}
            </Col>
          </div>
        </div>

        <div className='d-flex justify-content-between px-0 col-12 flex-row gap-1 flex-wrap dashboard-block'>
          <div className='p-0 d-flex' style={{ minWidth: '570px', flex: '1 1 570px' }}>
            <Col className='simple-box d-flex flex-column justify-content-center'>
              <Row className='ml-0'>
                <h5 className='title-simple-box text-uppercase mt-1 pl-0'>
                  {translations.find((trans) => trans.code === 'ICS128')?.text}
                </h5>
              </Row>

              <Col
                style={{ marginLeft: '-15px' }}
                className='sroi-details-padding d-flex justify-content-center align-items-center'>
                <Col className='padding-to-fit' style={{ minWidth: '507px' }}>
                  {avgData.length > 0 && !isDashboardLoading ? (
                    <MetricsTable
                      data={avgData}
                      columns={esgLabel.map((el) => ({ key: el, label: el }))}
                    />
                  ) : (
                    <div className='d-flex justify-content-center align-items-center'>
                      <Spinner animation='border' role='status' className='my-3'>
                        <span style={{ visibility: 'hidden' }}>Loading...</span>
                      </Spinner>
                    </div>
                  )}
                </Col>
              </Col>

              <div className='d-flex justify-content-end'>
                <span style={{ fontSize: '.65rem' }}>
                  {translations.find((trans) => trans.code === 'ICS130')?.text?.toUpperCase()}
                </span>
              </div>
            </Col>
          </div>
        </div>

        <Row className='report-second gap-3 d-flex'>
          <Col className='simple-box justify-content-center margins-reset-top'>
            <Row className='ml-0'>
              <h5 className='title-simple-box text-uppercase mt-1 pl-0'>
                {translations.find((trans) => trans.code === 'ICS129')?.text}
              </h5>
            </Row>

            <Row className='sroi-details-padding d-flex justify-content-center align-items-center mr-0'>
              <Col>
                {noOutData.length > 0 && !isDashboardLoading ? (
                  <MetricsTable data={noOutData.filter((el) => el.proponent !== 'portfolio')} />
                ) : (
                  <div className='d-flex justify-content-center align-items-center'>
                    <Spinner animation='border' role='status' className='my-3'>
                      <span style={{ visibility: 'hidden' }}>Loading...</span>
                    </Spinner>
                  </div>
                )}
              </Col>
            </Row>

            <div className='d-flex justify-content-end'>
              <span style={{ fontSize: '.65rem' }}>
                {translations.find((trans) => trans.code === 'ICS130')?.text?.toUpperCase()}
              </span>
            </div>
          </Col>
        </Row>

        <div className='col-12 p-0 gap-2 d-md-flex flex-column flex-xxl-row dashboard-block time-series-charts'>
          {esgTimeSeriesRating?.length > 0 && <EsgTimingBarChart data={esgTimeSeriesRating} />}
          {sroiTimeSeriesRating?.length > 0 && <SroiTimingBarChart data={sroiTimeSeriesRating} />}
        </div>

        <div className='d-flex justify-content-between px-0 col-12 flex-row gap-1 flex-wrap'>
          <div className='p-0 d-flex' style={{ minWidth: '570px', flex: '1 1 570px' }}>
            <Col className='simple-box d-flex flex-column justify-content-center'>
              <Row className='ml-0'>
                <h5 className='title-simple-box text-uppercase mt-1 pl-0'>
                  Score ESG Medio Ponderato per tipologia di intervento
                </h5>
              </Row>

              <Col
                style={{ marginLeft: '-15px' }}
                className='sroi-details-padding d-flex justify-content-center align-items-center'>
                <Col className='padding-to-fit mt-3' style={{ minWidth: '507px' }}>
                  {avgData.length > 0 && !isDashboardLoading ? (
                    <MetricsTable
                      data={financingSroiTable}
                      columns={sroiDetailColumns}
                      rowsPerColumn={5}
                      breakpoint={832}
                      showHeader={false}
                      valueRight={true}
                    />
                  ) : (
                    <div className='d-flex justify-content-center align-items-center'>
                      <Spinner animation='border' role='status' className='my-3'>
                        <span style={{ visibility: 'hidden' }}>Loading...</span>
                      </Spinner>
                    </div>
                  )}
                </Col>
              </Col>
            </Col>
          </div>
        </div>

        <div className='charts-grid dashboard-block'>
          <RegionalHeatmapChart
            containerId='projectNumberMapChart'
            title='Numero Progetti per regione'
            data={chartsData.projectsByRegion}
          />
          <RegionalHeatmapChart
            containerId='averageSroiMapChart'
            title='SROI Medio per regione'
            data={chartsData.sroiProjectsByRegion}
          />
          <SingleBarVerticalHistogram
            title='Investimento Medio per tipologia di proponente'
            containerId='averageInvestmentBarChart'
            data={chartsData.investmentByProponentType}
          />
          <SingleBarVerticalHistogram
            title='Sroi Medio per tipologia di proponente'
            containerId='averageSroiInvestmentBarChart'
            data={chartsData.sroiByProponentType}
          />
          <DistributionAverageVerticalHistogram
            title='Distribuzione dei progetti e degli investimenti per tipologia di proponente'
            data={chartsData.projectsDistribution}
          />
        </div>
      </div>

      <div className='simple-box scenaritab'>
        <div className='row d-flex align-items-center mb-3'>
          <div className='col-6'>
            <h5 className='title-simple-box'>Esplora progetti</h5>
          </div>
          <div className='col-6 text-right'>
            <ButtonToolbar className='justify-content-end align-items-stretch'>
              <Button variant='info' href='/valutazione' className='lineheight-initial'>
                <Row className='align-items-center py-1 px-3'>
                  <IcoPlus className='light-color ico-small plus mr-1' />
                  <span className='d-md-inline'>Nuovo Progetto</span>
                </Row>
              </Button>
              <OverlayTrigger placement='top' overlay={downloadTooltip}>
                <Button
                  variant='info'
                  onClick={!isLoading ? handleDownload : null}
                  aria-label='Download Excel'
                  className='ml-2 lineheight-initial'
                  disabled={isLoading}>
                  <Row
                    className='align-items-center justify-content-center'
                    style={{ padding: '0.25rem 0.7rem' }}>
                    {isLoading ? (
                      'Loading…'
                    ) : (
                      <DownloadFile width='23px' className='light-color m-0' />
                    )}
                  </Row>
                </Button>
              </OverlayTrigger>
              <InputGroup className='search-table align-items-center lineheight-initial'>
                <FormControl
                  type='text'
                  id='filter-text-box'
                  placeholder='Search...'
                  className='d-none d-md-block'
                  onInput={(e) => onFilterTextBoxChanged(e)}
                />
                <Lens />
              </InputGroup>
            </ButtonToolbar>
          </div>
        </div>

        <div className='row'>
          <div className='col-12' id='grid-wrapper'>
            {Object.keys(stats).length > 0 ? (
              <div className='ag-theme-alpine projects-list-table'>
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={projects}
                  defaultColDef={defaultColDef}
                  domLayout='autoHeight'
                  tooltipShowDelay={0}
                  tooltipHideDelay={2000}
                  tooltipMouseTrack={true}
                  onFirstDataRendered={onFirstDataRendered}
                  onGridReady={onGridReady}
                  onModelUpdated={onModelUpdated}
                  pagination={true}
                  paginationPageSize={20}
                  headerHeight={50}
                  rowHeight={50}
                  localeText={localeText}
                />
                <div className='example-header'>
                  Progetti per pagina:&nbsp;
                  <select onChange={onPageSizeChanged} id='page-size'>
                    {optionsPagination}
                  </select>
                </div>
              </div>
            ) : (
              <div className='text-center my-4'>
                <Spinner animation='border' role='status'>
                  <span style={{ visibility: 'hidden' }}>Loading...</span>
                </Spinner>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectsTab;
